import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Link2, Users } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600", queued: "bg-amber-100 text-amber-800", scheduled: "bg-amber-100 text-amber-800",
  sent: "bg-emerald-100 text-emerald-800", inProcess: "bg-blue-100 text-blue-800", suspended: "bg-red-100 text-red-800",
};

export default function BrevoCampaignDetail() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["brevo-campaign", bid],
    queryFn: async () => (await api.get(`/marketing/brevo/campaigns/${bid}`)).data,
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const c = data?.campaign || {};
  const s = data?.stats || {};
  const KPIS = [
    ["Sent", s.sent], ["Delivered", s.delivered], ["Unique opens", s.unique_opens], ["Open rate", pct(s.open_rate)],
    ["Unique clicks", s.unique_clicks], ["Click rate", pct(s.click_rate)],
    ["Bounces", (s.hard_bounces || 0) + (s.soft_bounces || 0)], ["Unsubscribes", s.unsubscribes],
  ];
  const recipients = data?.recipients || [];

  return (
    <div data-testid="brevo-campaign-detail">
      <Button variant="ghost" size="sm" className="mb-3 gap-1.5 text-slate-500 -ml-2" onClick={() => navigate(-1)} data-testid="brevo-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to campaigns
      </Button>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="brevo-detail-title">{c.name || "Campaign"}</h1>
          <p className="text-sm text-slate-500">{c.subject}</p>
        </div>
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_STYLE[c.status] || "bg-slate-100 text-slate-600"}`}>{c.status}</span>
        {c.sent_date && <span className="text-sm text-slate-500">sent {formatDate(c.sent_date)}</span>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
        {KPIS.map(([label, v]) => (
          <Card key={label} className="p-3.5 text-center" data-testid={`brevo-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="text-2xl font-bold font-heading text-slate-900">{(typeof v === "number" ? v.toLocaleString("en-IN") : v) ?? 0}</p>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">{label}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="brevo-links-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Link clicks</p>
          {Object.keys(data?.links || {}).length === 0 ? (
            <p className="text-sm text-slate-400 py-4">No link click data.</p>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(data.links).map(([url, n]) => (
                <div key={url} className="flex justify-between gap-3 text-sm py-1 border-b border-slate-50 last:border-0">
                  <span className="truncate text-slate-700" title={url}>{url}</span>
                  <span className="font-mono font-semibold shrink-0">{typeof n === "object" ? n.clickers ?? n.clicks ?? "" : n}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 lg:col-span-2" data-testid="brevo-recipients-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Per-recipient events ({recipients.length})</p>
          {recipients.length === 0 ? (
            <p className="text-sm text-slate-400 py-4">{data?.recipient_note || "No recipient events yet."} Events stream in via the Brevo webhook once it is set up (Brevo Hub → Set up webhooks).</p>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  {["Recipient", "CRM record", "Opens", "Clicks", "Last activity"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {recipients.map((r) => (
                    <TableRow key={r.email} data-testid={`brevo-recipient-${r.email}`}>
                      <TableCell className="font-mono text-xs">{r.email}</TableCell>
                      <TableCell>
                        {r.crm.length === 0 ? <span className="text-slate-300 text-xs">—</span> : r.crm.map((x) => (
                          <span key={`${x.type}-${x.id}`} className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-semibold text-slate-600 mr-1 capitalize">{x.type}: {x.name}</span>
                        ))}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{r.opens}</TableCell>
                      <TableCell className="font-mono text-sm">{r.clicks}{r.events.filter((e) => e.link).slice(-1).map((e) => <span key="l" className="text-[10px] text-slate-400 block truncate max-w-[180px]">{e.link}</span>)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{r.events.length ? `${r.events[r.events.length - 1].event} · ${formatDate(r.events[r.events.length - 1].at)}` : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
